import React, { useCallback } from "react";
import { FocusContext, useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { useVirtualization } from "./hooks/useVirtualization";
import { getModelConfig } from "../utils/models";
import { SECTION_TYPES } from "./constants/event";
import EventCardEPG from "./EPGEventCard";
import EPGPlayingNextCard from "./EPGPlayingNextCard";
import EPGSportCard from "./EPGSportCard";
import EPGLeagueCard from "./EPGLeagueCard";
import EPGPartnerCard from "./EPGPartnerCard";
import { scrollIntoViewWithinContainer } from "./utils/shared/scrollIntoView";
import { canPlayFromPlayingNext } from "./utils/shared/eventStatus";
import "./epg.scss";

const ROW_CLASS_NAMES = {
    [SECTION_TYPES.EVENTS]: "sectionRowWrapper",
    [SECTION_TYPES.PLAYING_NEXT]: "sectionRowWrapper",
};

const EPGSectionRow = React.memo(({ items, title, rowId, rowType, virtualizationParams, onEnterPress, activeLiveContentId, activeLiveContentType, subscribingEventId, onPlayingNextPlay }) => {
    const { ref, focusKey } = useFocusable({
        onFocus: () => {
            if (ref.current) {
                if (getModelConfig().epg.customScrollBehavior) {
                    scrollIntoViewWithinContainer(ref.current, 'scrollingRows');
                } else {
                    ref.current.scrollIntoView({
                        behavior: getModelConfig().epg.scrollBehavior,
                        block: "nearest",
                        inline: "nearest",
                    });
                }
            }
        },
    });
    const { scrollingRef, contentStyle, virtualItems, onItemFocus } = useVirtualization({
        items,
        ...virtualizationParams,
    });

    const boundOnEnterPress = useCallback((fp) => onEnterPress(fp, rowType), [onEnterPress, rowType]);

    const hasPlayNowButton = rowType === SECTION_TYPES.PLAYING_NEXT && items.some(canPlayFromPlayingNext);
    const rowClassName = hasPlayNowButton ? "sectionRowWrapper playingNextRowWrapper" : ROW_CLASS_NAMES[rowType] ?? "shortcutRowWrapper";

    const renderCard = (item, index) => {
        switch (rowType) {
            case SECTION_TYPES.PLAYING_NEXT:
                return <EPGPlayingNextCard event={item} key={item.id} index={index} rowId={rowId} onFocus={onItemFocus} onEnterPress={boundOnEnterPress} onPlayNow={onPlayingNextPlay} isLoading={item.id === subscribingEventId} />;
            case SECTION_TYPES.EVENTS:
                return <EventCardEPG event={item} key={item.id} index={index} rowId={rowId} onFocus={onItemFocus} onEnterPress={boundOnEnterPress} activeLiveContentId={activeLiveContentId} activeLiveContentType={activeLiveContentType} />;
            case SECTION_TYPES.SPORTS:
                return <EPGSportCard sport={item} key={item.id} index={index} rowId={rowId} onFocus={onItemFocus} onEnterPress={boundOnEnterPress} />;
            case SECTION_TYPES.LEAGUES:
                return <EPGLeagueCard league={item} key={item.id} index={index} rowId={rowId} onFocus={onItemFocus} onEnterPress={boundOnEnterPress} />;
            case SECTION_TYPES.PARTNERS:
                return <EPGPartnerCard partner={item} key={item.id} index={index} rowId={rowId} onFocus={onItemFocus} onEnterPress={boundOnEnterPress} />;
            default:
                return null;
        }
    };

    return (
        <FocusContext.Provider value={focusKey}>
            <div ref={ref} className={`sectionContainer ${rowClassName}`}>
                <h2 className="rowTitle">{title}</h2>
                <div ref={scrollingRef} className="sectionRowScrollingWrapper">
                    <div className="sectionRowScrollingContent" style={contentStyle}>
                        {virtualItems.map(({ item, index }) => renderCard(item, index))}
                    </div>
                </div>
            </div>
        </FocusContext.Provider>
    );
});

export default EPGSectionRow;
